/**
 * RP04 — Vocabulaire unique (docs/REVUE-PRODUIT-2026-09/09-VOCABULAIRE.md, D20, D34).
 *
 * Fichier UNIQUE des libellés affichés au bureau. Les clés techniques (en base, dans
 * l'API) ne changent pas : ce fichier ne fait que les traduire pour l'écran.
 * Tout nouveau texte visible doit venir d'ici ou respecter 09-VOCABULAIRE.
 * Garde-fou : backend/tests/isolated/rp04_vocabulaire.py refuse les mots de la
 * section 7 (CRC, Kimi, Claude, trigger, eIDAS…) dans le texte des .jsx.
 *
 * Miroir terrain : terrain/src/utils/libelles.js (garder les deux alignés).
 */

// ---------------------------------------------------------------------------
// Objets (§1)
// ---------------------------------------------------------------------------
export const OBJETS = {
  chantier: 'Chantier',
  chantiers: 'Chantiers',
  observation: 'Observation',
  observations: 'Observations',
  action: 'Action',
  actions: 'Actions',
  reserve: 'Réserve',
  reserves: 'Réserves',
  decision: 'Décision',
  reunion: 'Réunion',
  reunions: 'Réunions',
  compteRendu: 'Compte rendu',
  comptesRendus: 'Comptes rendus',
  cr: 'CR',
  document: 'Document',
  planningReference: 'Planning de référence',
  // R4 (RP47-50) — réception des travaux
  reception: 'Réception',
  receptionTravaux: 'Réception des travaux',
  pvReception: 'PV de réception',
};

// Type d'un élément capté (`elements.type`) : ce qui est capté est une Observation ;
// elle devient une Action (type `tache`), une Réserve, une Décision… (reco B de Q14).
export const TYPE_ELEMENT = {
  reserve: 'Réserve',
  tache: 'Action',
  action: 'Action',
  incident: 'Incident',
  non_conformite: 'Non-conformité',
  observation: 'Observation',
  validation: 'Validation',
  decision: 'Décision',
  demande_client: 'Demande du client',
  question_entreprise: 'Question',
  info: 'Info',
  autre: 'Autre',
  // Fluidité B4 (P5#17) — demande en garantie de parfait achèvement
  gpa: 'Garantie (GPA)',
};

/** « Compte rendu n° 12 » (ou « Compte rendu » si pas de numéro). */
export const libelleCompteRendu = (numero) =>
  (numero || numero === 0 ? `Compte rendu n° ${numero}` : 'Compte rendu');

/** Titre enregistré d'un compte rendu : les anciens « CRC n°3 » s'affichent « CR n°3 » (données inchangées). */
export const titreCompteRendu = (titre) => String(titre || '').replace(/^\s*CRC\b/, 'CR');

/** Forme courte : « CR n° 12 ». */
export const libelleCR = (numero) => (numero || numero === 0 ? `CR n° ${numero}` : 'CR');

// ---------------------------------------------------------------------------
// Statuts (§3)
// ---------------------------------------------------------------------------
// Action / élément terrain (`elements.etat`, `tasks.status`).
export const STATUT_ACTION = {
  a_valider: 'À trier', // B5 — proposition pas encore validée (≠ levée « À vérifier »)
  actif: 'À faire',
  a_faire: 'À faire',
  todo: 'À faire',
  en_cours: 'En cours',
  in_progress: 'En cours',
  a_verifier: 'À vérifier',
  fait: 'Levée',
  done: 'Levée',
  terminee: 'Levée',
  termine: 'Levée',
  leve: 'Levée',
  levee: 'Levée',
  ignore: 'Sans suite',
  archivee: 'Sans suite',
};

export const STATUT_RESERVE = {
  ouverte: 'Ouverte',
  actif: 'Ouverte',
  a_verifier: 'À vérifier',
  a_valider: 'À trier',
  levee: 'Levée',
  fait: 'Levée',
};

// Compte rendu (statut canonique renvoyé par l'API : voir crc/crcStatut.js).
export const STATUT_COMPTE_RENDU = {
  brouillon: 'Brouillon',
  valide: 'À relire',
  a_relire: 'À relire',
  publie: 'Diffusé (en ligne)',
  diffuse: 'Diffusé',
  signe: 'Accepté',
  accepte: 'Accepté',
  conteste: 'Contesté',
};

// Offre d'une entreprise (réponse à un appel d'offres).
export const STATUT_OFFRE = {
  a_chiffrer: 'À chiffrer',
  en_attente: 'À chiffrer',
  brouillon: 'À chiffrer',
  soumise: 'Envoyée',
  soumis: 'Envoyée',
  envoyee: 'Envoyée',
  declinee: 'Pas de réponse',
  decline: 'Pas de réponse',
  non_soumis: 'Pas de réponse',
};

export const STATUT_AVENANT = {
  propose: 'Proposé',
  accepte: 'Accepté par le MOA',
  refuse: 'Refusé',
};

export const STATUT_SITUATION = {
  deposee: 'Déposée',
  soumise: 'Déposée',
  verifiee: 'Vérifiée',
  certifiee: 'Certifiée',
  validee: 'Certifiée',
  payee: 'Payée',
};

// Journal des e-mails envoyés (`email_logs.status`, valeurs historiques en capitales).
export const STATUT_EMAIL = {
  'ENVOYÉ': 'Envoyé',
  ENVOYE: 'Envoyé',
  sent: 'Envoyé',
  'ÉCHEC': "Échec d'envoi",
  ECHEC: "Échec d'envoi",
  failed: "Échec d'envoi",
  'BLOQUÉ_TRIGGER_OFF': 'Non envoyé (e-mail désactivé)',
  BLOQUE_TRIGGER_OFF: 'Non envoyé (e-mail désactivé)',
  'BLOQUÉ_TRIGGER_ABSENT': 'Non envoyé (e-mail désactivé)',
  BLOQUE_TRIGGER_ABSENT: 'Non envoyé (e-mail désactivé)',
  blocked_mode_preparation: "Non envoyé (envois d'e-mails en pause)",
  en_file_mode_preparation: "Non envoyé (envois d'e-mails en pause)",
  notification_desactivee: 'Non envoyé (e-mail désactivé)',
  envoye: 'Envoyé',
  echec: "Échec d'envoi",
};

/** Famille d'un statut d'e-mail, pour la couleur du badge. */
export const familleStatutEmail = (statut) => {
  const l = STATUT_EMAIL[statut] || '';
  if (l === 'Envoyé') return 'ok';
  if (l.startsWith('Échec')) return 'echec';
  return 'bloque';
};

/** Libellé d'un statut, avec repli lisible (jamais la clé brute en capitales). */
export const libelleStatut = (table, cle) => {
  if (cle === null || cle === undefined || cle === '') return '';
  if (table[cle]) return table[cle];
  const s = String(cle).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// ---------------------------------------------------------------------------
// Personnes et accès (§4)
// ---------------------------------------------------------------------------
// Rôle global du compte (`users.role`).
export const ROLE_UTILISATEUR = {
  admin: 'Administrateur du cabinet',
  architect: 'Associé',
  collaborateur: 'Collaborateur', // RP32
  amo: 'AMO',
  intervenant: 'Partenaire',
  bet: 'Partenaire',
  client: 'Client (MOA)',
  entreprise: 'Entreprise',
  employe_entreprise: "Salarié de l'entreprise",
};

// R7 (RP84-86) — statut d'un compte dans « Équipe & accès » (backend :
// services/equipe_acces_service.py LIBELLES_STATUT).
export const STATUT_COMPTE = {
  actif: 'Actif',
  invite: 'Invité, pas encore activé',
  invitation_expiree: 'Invitation expirée',
  desactive: 'Désactivé',
};

// Onglets de l'écran unique « Équipe & accès » (09-VOCABULAIRE §4).
export const ONGLETS_EQUIPE_ACCES = [
  { id: 'cabinet', nom: 'Cabinet' },
  { id: 'partenaires', nom: 'Partenaires' },
  { id: 'entreprises', nom: 'Entreprises' },
  { id: 'clients', nom: 'Clients' },
];

// Rôle sur un chantier (`chantier_members.role_projet`).
export const ROLE_PROJET = {
  archi: 'Associé',
  moe_principal: 'Chef de projet',
  moe_membre: 'Collaborateur',
  consultant: 'Partenaire',
  amo: 'AMO', // RP40 / D7 — côté MOA
  client: 'Client (MOA)',
  entreprise: 'Entreprise',
};

// RP38 / D6 — spécialités du partenaire (miroir de backend/services/partenaire_amo_service.py).
// L'OPC modifie le planning ; l'économiste peut recevoir le droit « Voir les finances ».
export const SPECIALITES_PARTENAIRE = {
  structure: 'BET structure',
  fluides: 'BET fluides',
  thermique: 'BET thermique',
  economiste: 'Économiste',
  ct: 'Contrôleur technique',
  sps: 'Coordonnateur SPS',
  opc: 'OPC',
  autre: 'Autre',
};

/** « Partenaire — BET structure » quand la spécialité est connue. */
export const libellePartenaire = (specialite) => {
  const lib = SPECIALITES_PARTENAIRE[specialite] || specialite;
  return lib ? `Partenaire — ${lib}` : 'Partenaire';
};

/** Rôle (clé globale ou de chantier) → libellé ; un texte libre (« BET Structure ») est rendu tel quel. */
export const libelleRole = (role) => ROLE_UTILISATEUR[role] || ROLE_PROJET[role] || role || '';

export const libelleRoleUtilisateur = (role) => ROLE_UTILISATEUR[role] || libelleStatut({}, role);
export const libelleRoleProjet = (role, specialite) =>
  (role === 'consultant' ? libellePartenaire(specialite) : (ROLE_PROJET[role] || libelleStatut({}, role)));

// ---------------------------------------------------------------------------
// Phases (§6) — registre cabinet et registre public
// ---------------------------------------------------------------------------
export const PHASES_CABINET = {
  ESQ: 'Esquisse',
  APS: 'Avant-projet sommaire',
  APD: 'Avant-projet définitif',
  PC: 'Permis de construire',
  PRO: 'Projet',
  DCE: 'Dossier de consultation',
  ACT: 'Consultation',
  VISA: 'Visa',
  DET: 'Direction des travaux',
  EXE: 'Travaux',
  AOR: 'Réception',
  OPR: 'Réception',
  GPA: 'Garantie de parfait achèvement',
};

export const PHASES_PUBLIC = {
  ESQ: 'Conception',
  APS: 'Conception',
  APD: 'Conception',
  PRO: 'Conception',
  PC: 'Permis de construire',
  DCE: 'Choix des entreprises',
  ACT: 'Choix des entreprises',
  VISA: 'Travaux',
  DET: 'Travaux',
  EXE: 'Travaux',
  AOR: 'Réception des travaux',
  OPR: 'Réception des travaux',
  GPA: 'Garantie de parfait achèvement (1 an)',
};

// B1 — Étape unique du chantier (services/etape_service.py) : une seule liste, lue par le
// tableau de bord, la page du chantier, la synthèse et l'écran du client. La phase MOP
// (ESQ … AOR) n'est qu'un détail facultatif à l'intérieur d'une étape.
export const ETAPES_CHANTIER = [
  { cle: 'etudes', libelle: 'Études', public: 'Conception', phases: ['ESQ', 'APS', 'APD', 'PRO'] },
  { cle: 'permis', libelle: 'Permis', public: 'Permis de construire', phases: ['PC'] },
  { cle: 'consultation', libelle: 'Consultation', public: 'Choix des entreprises', phases: ['DCE', 'ACT'] },
  { cle: 'chantier', libelle: 'Chantier', public: 'Travaux', phases: ['VISA', 'DET', 'EXE'] },
  { cle: 'reception', libelle: 'Réception', public: 'Réception des travaux', phases: ['AOR', 'OPR'] },
  { cle: 'gpa', libelle: 'GPA', public: 'Garantie de parfait achèvement (1 an)', phases: ['GPA'] },
  { cle: 'cloture', libelle: 'Clôturé', public: 'Chantier terminé', phases: [] },
];

// Colonnes du tableau de bord : les étapes en cours ; « Clôturé » est rangé à part.
export const ETAPES_KANBAN = ETAPES_CHANTIER.filter((e) => e.cle !== 'cloture');

// Repli quand l'étape n'est pas encore enregistrée (anciens chantiers) : d'après la phase,
// puis le statut. « Terminé » est rangé à part (Clôturé), plus dans la colonne Chantier.
const ETAPE_PAR_STATUT = {
  prospection: 'etudes',
  en_cours: 'chantier',
  en_pause: 'chantier',
  termine: 'cloture',
};

/** Étape d'un chantier : champ `etape` (serveur), sinon d'après la phase puis le statut. */
export const etapeChantier = (chantier) => {
  // Fluidité B4 (P5#16, #19) — un chantier clôturé sort des colonnes actives
  if (chantier?.cloture_le) return 'cloture';
  if (ETAPES_CHANTIER.some((x) => x.cle === chantier?.etape)) return chantier.etape;
  if (chantier?.status === 'termine') return 'cloture';
  const phase = String(chantier?.current_phase || '').toUpperCase();
  const e = ETAPES_CHANTIER.find((x) => x.phases.includes(phase));
  if (e) return e.cle;
  return ETAPE_PAR_STATUT[chantier?.status] || 'etudes';
};

/** Libellé de l'étape (registre cabinet, ou public pour le client). */
export const libelleEtape = (cle, publicReg = false) => {
  const e = ETAPES_CHANTIER.find((x) => x.cle === cle) || ETAPES_CHANTIER[0];
  return publicReg ? e.public : e.libelle;
};

// Classe de couleur d'une étape (badges : en-tête du chantier, liste, tableau de bord).
export const ETAPE_CLASSE = {
  etudes: 'phase-esq',
  permis: 'phase-aps',
  consultation: 'phase-dce',
  chantier: 'phase-exe',
  reception: 'phase-opr',
  gpa: 'phase-aor',
  cloture: 'badge-termine',
};

// Statut déduit de l'étape : seuls « Prospection » (en Études) et « En pause » se montrent à côté.
export const STATUT_CHANTIER = {
  prospection: 'Prospection',
  en_cours: 'En cours',
  en_pause: 'En pause',
  termine: 'Terminé',
};

// ---------------------------------------------------------------------------
// Fluidité B2 — « Un mot, un endroit » : navigation du bureau
// ---------------------------------------------------------------------------
// Menu de gauche (un seul mot : « Chantiers », « Ma semaine »).
export const MENU = {
  tableauDeBord: 'Tableau de bord',
  chantiers: 'Chantiers',
  maSemaine: 'Ma semaine',
  appelsOffres: "Appels d'offres",
  entreprises: 'Entreprises',
  equipeAcces: 'Équipe & accès',
  administration: 'Administration',
  parametres: 'Paramètres',
  monCompte: 'Mon compte',
  monProfil: 'Mon profil',
};

// Les 6 onglets d'un chantier (+ Documents), dans l'ordre du chantier.
export const ONGLETS_CHANTIER_LIBELLES = {
  synthese: 'Synthèse',
  conception: 'Conception',
  marches: 'Marchés',
  chantier: 'Chantier',
  argent: 'Argent',
  reception: 'Réception',
  documents: 'Documents',
};

// Les vues regroupées sous « Chantier » et « Argent ».
export const VUES_CHANTIER_LIBELLES = {
  reunions: 'Réunions',
  crc: 'Comptes rendus',
  actions: 'Actions',
  visas: 'Visas',
  plans: 'Plans',
  galerie: 'Galerie',
  planning: 'Planning',
  finances: 'Finances',
  paiements: 'Paiements',
  situations: 'Mes situations',
  'point-moa': 'Point MOA',
};

// Partenaire (BET) : deux listes distinctes (P3#30).
export const LOTS_PARTENAIRE = {
  aViser: 'Lots à viser',        // ex-« Mes lots » (visas du partenaire)
  aRediger: 'Lots à rédiger',    // ex-« Lots confiés » (DPGF / CCTP délégués en conception)
  aRedigerARelire: 'Lots à rédiger, à relire',
};

// Une seule entrée « Importer » (P2#17) : ce que l'on peut importer.
export const IMPORTS = {
  titre: 'Que voulez-vous importer ?',
  bouton: 'Importer',
  dpgfWord: 'DPGF (Word)',
  dpgfExcel: 'DPGF (Excel)',
  dpgfDocuments: 'DPGF à partir des documents du chantier',
  devis: "Devis d'une entreprise (PDF ou photo)",
  chantierEnCours: 'Chantier déjà commencé (lots, marchés, factures)',
  comptesRendus: 'Comptes rendus antérieurs (PDF)',
  whatsapp: 'Conversation WhatsApp',
  documents: 'Documents et plans',
};

// Catégories de documents proposées selon l'étape (P1#17) : au dépôt, la liste
// commence par celles de l'étape ; les 22 catégories restent accessibles sous
// « Autres catégories ». Codes : backend/config.py › DEFAULT_CATEGORIES.
export const CODES_CATEGORIES_STANDARD = [
  'dpgf', 'cctp', 'dce', 'plans', 'pv', 'facture', 'devis', 'os', 'situation', 'assurance',
  'attestation', 'contrat', 'diagnostic', 'courrier', 'photo', 'doe', 'diuo', 'permis',
  'pv_reception', 'remise_cles', 'suivi_chantier', 'autre',
];
export const CATEGORIES_DOCUMENTS_PAR_ETAPE = {
  etudes: ['plans', 'diagnostic', 'permis', 'contrat', 'courrier', 'photo', 'autre'],
  consultation: ['dpgf', 'cctp', 'dce', 'plans', 'devis', 'courrier', 'autre'],
  chantier: ['plans', 'pv', 'os', 'situation', 'facture', 'devis', 'attestation', 'assurance', 'photo', 'suivi_chantier', 'courrier', 'autre'],
  reception: ['pv_reception', 'doe', 'diuo', 'remise_cles', 'situation', 'photo', 'courrier', 'autre'],
  gpa: ['doe', 'diuo', 'pv_reception', 'facture', 'photo', 'courrier', 'autre'],
};
export const GROUPES_CATEGORIES = {
  proposees: 'Proposées pour cette étape',
  autres: 'Autres catégories',
};

/**
 * Sépare les catégories en { proposees, autres } selon l'étape. Les catégories créées
 * par le cabinet ou pour ce chantier (code hors des 22) sont toujours proposées.
 * Sans étape connue : tout est proposé (rien n'est caché).
 */
export const categoriesSelonEtape = (categories, etape) => {
  const liste = categories || [];
  const codes = CATEGORIES_DOCUMENTS_PAR_ETAPE[etape];
  if (!codes) return { proposees: liste, autres: [] };
  const estProposee = (c) => codes.includes(c.code) || !CODES_CATEGORIES_STANDARD.includes(c.code);
  const proposees = liste.filter(estProposee)
    .sort((a, b) => {
      const ia = codes.indexOf(a.code); const ib = codes.indexOf(b.code);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  return { proposees, autres: liste.filter((c) => !estProposee(c)) };
};

// ---------------------------------------------------------------------------
// Boutons et libellés d'action (§5)
// ---------------------------------------------------------------------------
export const BOUTONS = {
  validerEtDiffuser: 'Valider et diffuser',
  soumettreArchitecte: "Soumettre à l'architecte",
  modifierCompteRendu: 'Modifier le compte rendu',
  nouveauCompteRendu: 'Nouveau compte rendu',
  telecharger: 'Télécharger (PDF / Word)',
  telechargerWord: 'Télécharger (Word)',
  telechargerPdf: 'Télécharger (PDF)',
  faitAvecPhoto: 'Fait, avec photo',
  validerLevee: 'Valider la levée',
  refuserLevee: 'Refuser la levée',
  signaler: 'Signaler',
  repondre: 'Répondre',
  jApprouve: "J'approuve",
  desaccord: 'Je signale un désaccord',
  proposerModification: 'Proposer une modification',
  relancer: 'Relancer',
  inviter: 'Inviter',
  renvoyerInvitation: "Renvoyer l'invitation",
  desactiverCompte: 'Désactiver le compte',
  reactiverCompte: 'Réactiver le compte',
  gererDepart: 'Gérer le départ',
  supprimerDefinitivement: 'Supprimer définitivement',
  // Fluidité B2 — « Action » partout (plus de « Tâche »)
  creerAction: 'Créer une action',
  nouvelleAction: 'Nouvelle action',
  importer: 'Importer',
};

// Fonctions de l'application (§5, §7).
export const FONCTIONS = {
  assistant: 'Assistant',
  emailsAutomatiques: 'E-mails automatiques',
  emailAutomatique: 'E-mail automatique',
  envoisEnPause: "Envois d'e-mails en pause",
  reglagesParDefaut: 'Réglages par défaut (appliqués d\'office)',
  signatureSms: 'Signature électronique par code SMS',
  droitConfie: 'Droit confié',
  demandezArchitecte: "Demandez à l'architecte",
  // RP28-31 (ARC-A1) — écran de la réunion hebdomadaire de chantier
  reunionSemaine: 'Réunion de la semaine',
  // RP26-27 (ENT-A6, D18) — e-mails d'une entreprise regroupés une fois par jour
  condenseQuotidien: 'Condensé quotidien',
  emailsImmediats: 'Au fil de l\'eau',
};
